import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import io from 'socket.io-client';
import { ASTERISK_API_BASE, CHAT_SOCKET_URL } from '../config/bridge';

const ChatContext = createContext();

const initialState = {
  activeSession: null,
  messages: {},
  isOnline: false,
  unreadCounts: {},
  typingUsers: {},
  agentDirectoryVersion: 0
};

const SESSION_CACHE_TTL_MS = 30000;
const HISTORY_CACHE_TTL_MS = 15000;

const requestChatHttp = async (path, { method = 'GET', body } = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${ASTERISK_API_BASE}${path}`, {
    method,
    cache: 'no-store',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {})
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {})
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || data.message || `Chat request failed (${response.status})`);
  }
  return data;
};

const safeJsonParse = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
};

const chatReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ONLINE':
      return { ...state, isOnline: true };
    case 'SET_OFFLINE':
      return { ...state, isOnline: false };
    case 'SET_ACTIVE_SESSION':
      return { ...state, activeSession: action.payload };
    case 'ADD_MESSAGE': {
      const sessionMessages = state.messages[action.payload.sessionId] || [];
      const incomingMessage = action.payload.message;
      const incomingId = String(incomingMessage?.id || incomingMessage?.Id || '').trim();
      const duplicateExists = sessionMessages.some((message) => {
        const existingId = String(message?.id || message?.Id || '').trim();
        if (incomingId && existingId && existingId === incomingId) {
          return true;
        }

        return (
          String(message?.senderId || '').trim() === String(incomingMessage?.senderId || '').trim() &&
          String(message?.receiverId || '').trim() === String(incomingMessage?.receiverId || '').trim() &&
          String(message?.messageText || '').trim() === String(incomingMessage?.messageText || '').trim() &&
          String(message?.createdAt || '').trim() === String(incomingMessage?.createdAt || '').trim()
        );
      });

      if (duplicateExists) {
        return state;
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.sessionId]: [...sessionMessages, incomingMessage]
        }
      };
    }
    case 'LOAD_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.sessionId]: action.payload.messages
        }
      };
    case 'REMOVE_MESSAGE': {
      const sessionMessages = state.messages[action.payload.sessionId] || [];
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.sessionId]: sessionMessages.filter(
            (message) => String(message.id) !== String(action.payload.messageId)
          )
        }
      };
    }
    case 'CLEAR_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.sessionId]: []
        }
      };
    case 'SET_UNREAD_COUNT':
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [action.payload.sessionId]: action.payload.count
        }
      };
    case 'SET_ALL_UNREAD_COUNTS':
      return {
        ...state,
        unreadCounts: action.payload
      };
    case 'SET_TYPING_USERS':
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.sessionId]: action.payload.users
        }
      };
    case 'BUMP_AGENT_DIRECTORY_VERSION':
      return {
        ...state,
        agentDirectoryVersion: state.agentDirectoryVersion + 1
      };
    default:
      return state;
  }
};

const readCurrentAgentIdentity = () => {
  const user = safeJsonParse(localStorage.getItem('user'), {});
  const extensionData = safeJsonParse(localStorage.getItem('extension'), {});

  return {
    userId: String(
      user?.UserID || user?.userId || user?.ID || user?.id || ''
    ).trim(),
    username: String(
      user?.FullName || user?.UserName || user?.username || user?.LoginName || user?.loginName || ''
    ).trim(),
    extension: String(extensionData?.extensionNumber || extensionData?.extension || '').trim()
  };
};

const normalizeIncomingMessage = (data = {}) => ({
  id: data.id || data.Id || `${data.sessionId || data.SessionId}-${Date.now()}`,
  sessionId: data.sessionId || data.SessionId || '',
  senderType: data.senderType || data.SenderType || '',
  senderId: String(data.senderId || data.SenderId || '').trim(),
  receiverId: String(data.receiverId || data.ReceiverId || '').trim(),
  messageText: data.messageText || data.MessageText || data.message || '',
  createdAt: data.createdAt || data.CreatedAt || new Date().toISOString(),
  deletedForEveryone: Boolean(data.deletedForEveryone || data.DeletedForEveryone || false),
  deletedAt: data.deletedAt || data.DeletedAt || null
});

const normalizeSessionRecord = (data = {}) => ({
  sessionId: String(data.sessionId || data.SessionId || '').trim(),
  participant1Id: String(data.participant1Id || data.Participant1Id || '').trim(),
  participant2Id: String(data.participant2Id || data.Participant2Id || '').trim(),
  unreadCount: Number(data.unreadCount || data.UnreadCount || 0) || 0,
  updatedAt: String(data.updatedAt || data.UpdatedAt || '').trim()
});

export const ChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const activeSessionRef = useRef(null);
  const socketRef = useRef(null);
  const typingUsersRef = useRef({});
  const unreadCountsRef = useRef({});
  const sessionsRef = useRef([]);
  const sessionsLoadedAtRef = useRef(0);
  const historyLoadedAtRef = useRef({});

  const waitForSocketConnection = useCallback(() => new Promise((resolve, reject) => {
    const socket = socketRef.current;
    if (!socket) {
      reject(new Error('Chat socket is not initialized'));
      return;
    }

    if (socket.connected) {
      resolve(socket);
      return;
    }

    const timeoutId = setTimeout(() => {
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      reject(new Error('Chat socket connection timed out'));
    }, 10000);

    const handleConnect = () => {
      clearTimeout(timeoutId);
      socket.off('connect_error', handleConnectError);
      resolve(socket);
    };

    const handleConnectError = (error) => {
      clearTimeout(timeoutId);
      socket.off('connect', handleConnect);
      reject(error instanceof Error ? error : new Error('Chat socket connection failed'));
    };

    socket.once('connect', handleConnect);
    socket.once('connect_error', handleConnectError);
  }), []);

  useEffect(() => {
    activeSessionRef.current = state.activeSession;
  }, [state.activeSession]);

  useEffect(() => {
    typingUsersRef.current = state.typingUsers;
  }, [state.typingUsers]);

  useEffect(() => {
    unreadCountsRef.current = state.unreadCounts;
  }, [state.unreadCounts]);

  const emitSocketRequest = useCallback(async (eventName, payload = {}) => {
    const socket = await waitForSocketConnection();

    return new Promise((resolve, reject) => {
      socket.timeout(15000).emit(eventName, payload, (error, response) => {
        if (error) {
          reject(new Error(`Chat socket request timed out for ${eventName}`));
          return;
        }

        if (response?.error) {
          reject(new Error(response.error));
          return;
        }

        resolve(response || {});
      });
    });
  }, [waitForSocketConnection]);

  const loadUnreadCounts = useCallback(async () => {
    const identity = readCurrentAgentIdentity();
    const currentAgentId = identity.extension || identity.userId || identity.username;
    if (!currentAgentId) return;

    if (Date.now() - sessionsLoadedAtRef.current < SESSION_CACHE_TTL_MS && sessionsRef.current.length > 0) {
      const unreadMap = sessionsRef.current.reduce((acc, session) => {
        if (session.sessionId) {
          acc[session.sessionId] = activeSessionRef.current === session.sessionId ? 0 : session.unreadCount;
        }
        return acc;
      }, {});
      dispatch({ type: 'SET_ALL_UNREAD_COUNTS', payload: unreadMap });
      return;
    }

    try {
      let data;
      if (socketRef.current?.connected) {
        try {
          data = await emitSocketRequest('fetch_chat_sessions', { userId: currentAgentId });
        } catch (error) {
          console.warn('Chat session socket request failed; using HTTP fallback:', error.message);
        }
      }
      if (!data) {
        data = await requestChatHttp(`/chat/persistent/sessions/${encodeURIComponent(currentAgentId)}`);
      }
      const normalizedSessions = (data.sessions || []).map(normalizeSessionRecord);
      sessionsRef.current = normalizedSessions;
      sessionsLoadedAtRef.current = Date.now();

      const unreadMap = normalizedSessions.reduce((acc, session) => {
        if (session.sessionId) {
          acc[session.sessionId] = activeSessionRef.current === session.sessionId ? 0 : session.unreadCount;
        }
        return acc;
      }, {});

      dispatch({ type: 'SET_ALL_UNREAD_COUNTS', payload: unreadMap });
    } catch (error) {
      console.error('Failed to load unread chat counts:', error);
    }
  }, [emitSocketRequest]);

  const findExistingSession = useCallback((participant1Id, participant2Id) => {
    const first = String(participant1Id || '').trim();
    const second = String(participant2Id || '').trim();
    if (!first || !second) {
      return null;
    }

    return sessionsRef.current.find((session) => (
      (session.participant1Id === first && session.participant2Id === second) ||
      (session.participant1Id === second && session.participant2Id === first)
    )) || null;
  }, []);

  useEffect(() => {
    const socket = io(CHAT_SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
      auth: {
        token: localStorage.getItem('token') || ''
      }
    });

    socketRef.current = socket;

    let registeredAgentId = '';
    const syncChatIdentity = () => {
      const token = localStorage.getItem('token') || '';
      if (socket.auth?.token !== token) {
        socket.auth = { ...(socket.auth || {}), token };
        if (!socket.connected && !socket.active) {
          socket.connect();
        }
      }

      const identity = readCurrentAgentIdentity();
      const agentRoomId = identity.extension || identity.userId || identity.username;
      if (socket.connected && agentRoomId && agentRoomId !== registeredAgentId) {
        socket.emit('register_chat_agent', agentRoomId);
        registeredAgentId = agentRoomId;
        sessionsLoadedAtRef.current = 0;
        loadUnreadCounts().catch(() => {});
      }
    };

    socket.on('connect', () => {
      registeredAgentId = '';
      syncChatIdentity();
      dispatch({ type: 'SET_ONLINE' });
      loadUnreadCounts().catch(() => {});
    });

    socket.on('disconnect', () => {
      dispatch({ type: 'SET_OFFLINE' });
    });

    socket.on('connect_error', (error) => {
      console.error('Chat socket connect error:', error?.message || error);
    });

    socket.on('receive_message', (payload) => {
      const message = normalizeIncomingMessage(payload);
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          sessionId: message.sessionId,
          message
        }
      });

      const identity = readCurrentAgentIdentity();
      const isOwnMessage =
        message.senderId === identity.extension ||
        message.senderId === identity.userId ||
        message.senderId === identity.username;

      if (isOwnMessage || activeSessionRef.current === message.sessionId) {
        dispatch({
          type: 'SET_UNREAD_COUNT',
          payload: {
            sessionId: message.sessionId,
            count: 0
          }
        });
      } else {
        const currentUnread = unreadCountsRef.current[message.sessionId] || 0;
        dispatch({
          type: 'SET_UNREAD_COUNT',
          payload: {
            sessionId: message.sessionId,
            count: currentUnread + 1
          }
        });
      }

      if (message.sessionId) {
        historyLoadedAtRef.current[message.sessionId] = Date.now();
      }
    });

    socket.on('user_typing', (data = {}) => {
      const sessionId = String(data.sessionId || data.SessionId || '').trim();
      const senderId = String(data.senderId || data.SenderId || '').trim();
      if (!sessionId || !senderId) return;

      const currentUsers = typingUsersRef.current?.[sessionId] || [];
      if (currentUsers.includes(senderId)) return;

      dispatch({
        type: 'SET_TYPING_USERS',
        payload: {
          sessionId,
          users: [...currentUsers, senderId]
        }
      });
    });

    socket.on('user_stopped_typing', (data = {}) => {
      const sessionId = String(data.sessionId || data.SessionId || '').trim();
      const senderId = String(data.senderId || data.SenderId || '').trim();
      const currentUsers = typingUsersRef.current?.[sessionId] || [];

      dispatch({
        type: 'SET_TYPING_USERS',
        payload: {
          sessionId,
          users: currentUsers.filter((id) => id !== senderId)
        }
      });
    });

    socket.on('message_deleted_everyone', (payload = {}) => {
      dispatch({
        type: 'REMOVE_MESSAGE',
        payload: {
          sessionId: payload.sessionId || payload.SessionId,
          messageId: payload.messageId || payload.MessageId
        }
      });
    });

    socket.on('message_error', (payload = {}) => {
      console.error('Chat socket error:', payload.error || payload);
    });

    socket.on('chat_agent_directory_updated', () => {
      dispatch({ type: 'BUMP_AGENT_DIRECTORY_VERSION' });
    });

    const identitySyncId = window.setInterval(syncChatIdentity, 2000);
    const unreadPollId = window.setInterval(() => {
      if (!socket.connected) {
        sessionsLoadedAtRef.current = 0;
        loadUnreadCounts().catch(() => {});
      }
    }, 15000);
    syncChatIdentity();

    return () => {
      window.clearInterval(identitySyncId);
      window.clearInterval(unreadPollId);
      socket.close();
      socketRef.current = null;
    };
  }, [loadUnreadCounts]);

  const startChat = useCallback(async (participant1Id, participant2Id, queryId = null, sessionType = 'agent-to-agent') => {
    const existingSession = findExistingSession(participant1Id, participant2Id);
    if (existingSession?.sessionId) {
      dispatch({ type: 'SET_ACTIVE_SESSION', payload: existingSession.sessionId });
      return {
        sessionId: existingSession.sessionId,
        SessionId: existingSession.sessionId,
        Participant1Id: existingSession.participant1Id,
        Participant2Id: existingSession.participant2Id,
        UnreadCount: existingSession.unreadCount
      };
    }

    const payload = {
      callerId: String(participant1Id || '').trim(),
      agentId: String(participant2Id || '').trim(),
      queryId,
      sessionType
    };
    let data;
    if (socketRef.current?.connected) {
      try {
        data = await emitSocketRequest('start_chat', payload);
      } catch (error) {
        console.warn('Chat start socket request failed; using HTTP fallback:', error.message);
      }
    }
    if (!data) {
      data = await requestChatHttp('/chat/persistent/start', { method: 'POST', body: payload });
    }

    const sessionId = data.sessionId || data.SessionId;
    const normalizedSession = normalizeSessionRecord(data);
    if (normalizedSession.sessionId) {
      sessionsRef.current = [
        normalizedSession,
        ...sessionsRef.current.filter((session) => session.sessionId !== normalizedSession.sessionId)
      ];
      sessionsLoadedAtRef.current = Date.now();
    }
    dispatch({ type: 'SET_ACTIVE_SESSION', payload: sessionId });
    return { ...data, sessionId };
  }, [emitSocketRequest, findExistingSession]);

  const startAgentChat = useCallback(
    async (currentAgentId, targetAgentId) => startChat(currentAgentId, targetAgentId, null, 'agent-to-agent'),
    [startChat]
  );

  const sendMessage = useCallback(async (sessionId, message, senderType, senderId, receiverId = '') => {
    const payload = {
      sessionId,
      senderType,
      senderId,
      receiverId,
      message
    };
    if (socketRef.current?.connected) {
      socketRef.current.emit('send_message', payload);
      return;
    }

    const savedMessage = normalizeIncomingMessage(await requestChatHttp(
      `/chat/persistent/${encodeURIComponent(sessionId)}/messages`,
      { method: 'POST', body: payload }
    ));
    dispatch({
      type: 'ADD_MESSAGE',
      payload: { sessionId: savedMessage.sessionId, message: savedMessage }
    });
  }, []);

  const emitTypingStart = useCallback((sessionId, senderType, senderId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('typing_start', { sessionId, senderType, senderId });
  }, []);
  const emitTypingStop = useCallback((sessionId, senderType, senderId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('typing_stop', { sessionId, senderType, senderId });
  }, []);

  const joinChat = useCallback((sessionId) => {
    if (!sessionId) return;
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_chat', sessionId);
    }
    dispatch({ type: 'SET_ACTIVE_SESSION', payload: sessionId });
  }, []);

  const leaveChat = useCallback(() => {
    dispatch({ type: 'SET_ACTIVE_SESSION', payload: null });
  }, []);

  const loadChatHistory = useCallback(async (sessionId, userId = '') => {
    const cachedMessages = state.messages[sessionId] || [];
    const lastLoadedAt = historyLoadedAtRef.current[sessionId] || 0;
    if (cachedMessages.length > 0 && Date.now() - lastLoadedAt < HISTORY_CACHE_TTL_MS) {
      return cachedMessages;
    }

    let data;
    if (socketRef.current?.connected) {
      try {
        data = await emitSocketRequest('fetch_chat_history', { sessionId, userId });
      } catch (error) {
        console.warn('Chat history socket request failed; using HTTP fallback:', error.message);
      }
    }
    if (!data) {
      const params = new URLSearchParams();
      if (userId) params.set('userId', userId);
      data = await requestChatHttp(
        `/chat/persistent/${encodeURIComponent(sessionId)}/messages${params.toString() ? `?${params}` : ''}`
      );
    }

    const normalizedMessages = (data.messages || []).map(normalizeIncomingMessage);
    historyLoadedAtRef.current[sessionId] = Date.now();

    dispatch({
      type: 'LOAD_MESSAGES',
      payload: {
        sessionId,
        messages: normalizedMessages
      }
    });
    return normalizedMessages;
  }, [emitSocketRequest, state.messages]);

  const markAsRead = useCallback(async (sessionId, userId) => {
    sessionsRef.current = sessionsRef.current.map((session) => (
      session.sessionId === sessionId ? { ...session, unreadCount: 0 } : session
    ));
    if (socketRef.current?.connected) {
      try {
        await emitSocketRequest('mark_read', { sessionId, userId });
      } catch (error) {
        console.warn('Chat mark-read socket request failed; using HTTP fallback:', error.message);
        await requestChatHttp(`/chat/persistent/${encodeURIComponent(sessionId)}/mark-read`, {
          method: 'POST',
          body: { userId }
        });
      }
    } else {
      await requestChatHttp(`/chat/persistent/${encodeURIComponent(sessionId)}/mark-read`, {
        method: 'POST',
        body: { userId }
      });
    }

    dispatch({
      type: 'SET_UNREAD_COUNT',
      payload: {
        sessionId,
        count: 0
      }
    });
  }, [emitSocketRequest]);

  const deleteMessageForMe = useCallback(async (sessionId, messageId, userId) => {
    await emitSocketRequest('delete_message_for_me', { sessionId, messageId, userId });

    dispatch({
      type: 'REMOVE_MESSAGE',
      payload: {
        sessionId,
        messageId
      }
    });
  }, [emitSocketRequest]);

  const deleteMessageForEveryone = useCallback(async (sessionId, messageId, userId) => {
    if (!socketRef.current?.connected) {
      throw new Error('Chat socket is not connected');
    }

    socketRef.current.emit('delete_message_everyone', {
      sessionId,
      messageId,
      userId
    });
  }, []);

  const clearChatForMe = useCallback(async (sessionId, userId) => {
    await emitSocketRequest('clear_chat_for_me', { sessionId, userId });

    dispatch({
      type: 'CLEAR_MESSAGES',
      payload: {
        sessionId
      }
    });
    dispatch({
      type: 'SET_UNREAD_COUNT',
      payload: {
        sessionId,
        count: 0
      }
    });
  }, [emitSocketRequest]);

  const getSessionSummary = useCallback((sessionId) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) {
      return null;
    }

    const session = sessionsRef.current.find((item) => item.sessionId === normalizedSessionId);
    if (!session) {
      return null;
    }

    const identity = readCurrentAgentIdentity();
    const currentAgentIds = new Set(
      [identity.extension, identity.userId, identity.username]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    );

    const participant1Id = String(session.participant1Id || '').trim();
    const participant2Id = String(session.participant2Id || '').trim();
    const peerId = currentAgentIds.has(participant1Id)
      ? participant2Id
      : currentAgentIds.has(participant2Id)
      ? participant1Id
      : participant2Id || participant1Id;

    return {
      ...session,
      peerId
    };
  }, []);

  const value = useMemo(() => ({
    ...state,
    socket: socketRef.current,
    startChat,
    startAgentChat,
    sendMessage,
    emitTypingStart,
    emitTypingStop,
    joinChat,
    leaveChat,
    loadChatHistory,
    markAsRead,
    deleteMessageForMe,
    deleteMessageForEveryone,
    clearChatForMe,
    getSessionSummary
  }), [
    state,
    startChat,
    startAgentChat,
    sendMessage,
    emitTypingStart,
    emitTypingStop,
    joinChat,
    leaveChat,
    loadChatHistory,
    markAsRead,
    deleteMessageForMe,
    deleteMessageForEveryone,
    clearChatForMe,
    getSessionSummary
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
